import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';

/* ===================================================================
   TYPES
   =================================================================== */

export interface Slide {
  slide_number: number;
  title: string;
  html: string;
}

interface SlideViewerProps {
  slides: Slide[];
  compact?: boolean;
}

/* ===================================================================
   STYLED COMPONENTS
   =================================================================== */

const ViewerRoot = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  margin-top: ${({ $compact }) => ($compact ? '0.5rem' : '0')};
`;

const SlideContainer = styled.div<{ $compact?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: ${({ $compact }) => ($compact ? '280px' : '540px')};
  background: #1a1a2e;
  overflow: hidden;
`;

const SlideFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  background: var(--bg-elevated);
  border-top: 1px solid var(--border);
  gap: 0.5rem;
`;

const NavBtn = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: ${({ $disabled }) => ($disabled ? 'transparent' : 'var(--bg-surface)')};
  color: ${({ $disabled }) => ($disabled ? 'var(--text-muted)' : 'var(--text-secondary)')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-size: 0.75rem;
  padding: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $disabled }) => ($disabled ? 'transparent' : 'var(--accent-subtle)')};
    color: ${({ $disabled }) => ($disabled ? 'var(--text-muted)' : 'var(--accent)')};
  }
`;

const SlideCounter = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  user-select: none;
`;

const SlideTitle = styled.span`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  font-weight: 500;
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  min-width: 0;
`;

/* ===================================================================
   COMPONENT
   =================================================================== */

const WRAPPER_TEMPLATE = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  html, body { margin: 0; padding: 0; width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    background: #1a1a2e; overflow: hidden; }
  body > div { transform-origin: top left; }
</style></head><body>__CONTENT__</body></html>`;

export const SlideViewer: React.FC<SlideViewerProps> = ({ slides, compact = false }) => {
  const [current, setCurrent] = useState(0);

  const sanitizedSlides = useMemo(
    () =>
      slides.map((s) => ({
        ...s,
        html: DOMPurify.sanitize(s.html, {
          FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input'],
          FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
        }),
      })),
    [slides],
  );

  if (sanitizedSlides.length === 0) return null;

  const slide = sanitizedSlides[current];
  const srcDoc = WRAPPER_TEMPLATE.replace('__CONTENT__', slide.html);

  return (
    <ViewerRoot $compact={compact}>
      <SlideContainer $compact={compact}>
        <SlideFrame
          srcDoc={srcDoc}
          sandbox="allow-same-origin"
          title={`Slide ${slide.slide_number}: ${slide.title}`}
        />
      </SlideContainer>
      <Controls>
        <NavBtn
          $disabled={current === 0}
          onClick={() => current > 0 && setCurrent(current - 1)}
          aria-label="Previous slide"
        >
          ◀
        </NavBtn>
        <SlideTitle>{slide.title}</SlideTitle>
        <SlideCounter>
          {current + 1} / {sanitizedSlides.length}
        </SlideCounter>
        <NavBtn
          $disabled={current === sanitizedSlides.length - 1}
          onClick={() => current < sanitizedSlides.length - 1 && setCurrent(current + 1)}
          aria-label="Next slide"
        >
          ▶
        </NavBtn>
      </Controls>
    </ViewerRoot>
  );
};

export default SlideViewer;
